import { enumMembers, type ApiSchema } from "../apiSchema";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";
import { readStringList } from "./contentDecoders";

/**
 * What blocks a merge (reviews.md R-3), as the review page and the editor's check read it:
 * the server's verdict and the shapes violations of a head, or of a draft, split against
 * its base. One decoder for both.
 */

export type CheckFinding = Readonly<Pick<ApiSchema<"CheckFindingBody">, "message" | "focus" | "rule" | "severity">>;
export interface ShapeViolation {
  readonly checkId: string;
  readonly checkTitle: string | null;
  readonly finding: CheckFinding;
}
/** A head's shapes violations split against its base (R-3): the review's, and the editor's for a draft. */
export interface ShapeViolations {
  readonly introduced: readonly ShapeViolation[];
  readonly preExisting: readonly ShapeViolation[];
  /** New violations the engine's counts prove beyond the listed ones: they block too. */
  readonly unlistedIntroduced: number;
  /** Checks neither the lists nor the counts could compare, or whose base did not run: they block nothing (R-3). */
  readonly uncomparedCheckIds: readonly string[];
}
/** The server's R-3 verdict; the web never infers it from the listed findings. */
/** An item the change keeps up to date that the merge would land out of date (ADR-0044). */
export interface MaintainedBlock {
  readonly itemId: string;
  readonly name: string | null;
  readonly fix: "rebuild" | "update_from_main" | "change_declaration";
  readonly problem: string | null;
}
/** Module C's rebuild of those items at the reviewed head: its state, its Run, and why it failed. */
export interface MaintainedRebuildStatus {
  readonly state: ApiSchema<"RebuildState"> | null;
  readonly threadId: string | null;
  readonly problem: string | null;
}
export interface MergeBlocks {
  readonly blocked: boolean;
  readonly inconsistencyCheckIds: readonly string[];
  readonly shapeViolations: ShapeViolations;
  readonly maintainedItems: readonly MaintainedBlock[];
  /** Kept-up-to-date items built from each other. */
  readonly maintainedCycle: readonly string[];
  readonly maintainedRebuild: MaintainedRebuildStatus | null;
}

export const readFindingMessage = (value: unknown): string => readString(readObject(value, "finding"), "message");
function readShapeViolation(value: unknown): ShapeViolation {
  const violation = readObject(value, "shape violation");
  const finding = readObject(violation["finding"], "finding");
  return {
    checkId: readString(violation, "check_id"), checkTitle: readNullableString(violation, "check_title"),
    finding: {
      message: readFindingMessage(finding), focus: readNullableString(finding, "focus"), rule: readNullableString(finding, "rule"),
      severity: readOneOf(finding, "severity", enumMembers<ApiSchema<"FindingSeverity">>({ violation: true, warning: true, info: true })),
    },
  };
}
export function readShapeViolations(value: unknown): ShapeViolations {
  const shapes = readObject(value, "shape violations");
  return {
    introduced: readArray(shapes["introduced"], "introduced violations", readShapeViolation),
    preExisting: readArray(shapes["pre_existing"], "pre-existing violations", readShapeViolation),
    unlistedIntroduced: readInteger(shapes, "unlisted_introduced"),
    uncomparedCheckIds: readStringList(shapes, "uncompared_check_ids"),
  };
}
const MAINTAINED_FIXES = ["rebuild", "update_from_main", "change_declaration"] as const;
function readMaintainedBlock(value: unknown): MaintainedBlock {
  const block = readObject(value, "maintained item block");
  return {
    itemId: readString(block, "item_id"), name: readNullableString(block, "name"),
    fix: readOneOf(block, "fix", MAINTAINED_FIXES), problem: readNullableString(block, "problem"),
  };
}

const REBUILD_STATES = enumMembers<ApiSchema<"RebuildState">>({ running: true, waiting_for_host: true, committed: true, failed: true, superseded: true });
function readMaintainedRebuild(value: unknown): MaintainedRebuildStatus | null {
  if (value === undefined || value === null) return null;
  const rebuild = readObject(value, "maintained rebuild");
  const problem = rebuild["problem"] === undefined || rebuild["problem"] === null ? null : readString(readObject(rebuild["problem"], "rebuild problem"), "message");
  return {
    state: rebuild["state"] === null ? null : readOneOf(rebuild, "state", REBUILD_STATES),
    threadId: readNullableString(rebuild, "thread_id"),
    problem,
  };
}

export function readMergeBlocks(value: unknown): MergeBlocks {
  const blocks = readObject(value, "merge blocks");
  return {
    blocked: readBoolean(blocks, "blocked"), inconsistencyCheckIds: readStringList(blocks, "inconsistency_check_ids"),
    shapeViolations: readShapeViolations(blocks["shape_violations"]),
    maintainedItems: blocks["maintained_items"] === undefined ? [] : readArray(blocks["maintained_items"], "maintained items", readMaintainedBlock),
    maintainedCycle: blocks["maintained_cycle"] === undefined ? [] : readStringList(blocks, "maintained_cycle"),
    maintainedRebuild: readMaintainedRebuild(blocks["maintained_rebuild"]),
  };
}
